import { recordingDeviceCopy } from '@/features/recording-device-metadata';

/**
 * Strings of the "Audio initial" and "Texte initial" pages, FR and EN, written
 * for the person answering (contributor or collector on a community profile).
 * The admin wording of translations.ts (t.admin.initialDataSession) is no
 * longer read here. Placeholders in braces are filled with `fill` (copy.ts).
 */
export const initialDataCopy = {
  fr: {
    audioTitle: "Répondez à l'oral",
    textTitle: "Répondez par écrit",
    audioSubtitle: "Chaque question est écrite en français. Répondez à voix haute dans votre langue, une question à la fois.",
    textSubtitle: "Chaque question est écrite en français. Écrivez la réponse dans votre langue, une question à la fois.",
    collectorNotice: "Vous répondez pour le profil {code}",
    collectorNoticeBody: "Les réponses envoyées sont rattachées à ce profil communautaire.",
    statQuestions: "Questions",
    statMinutes: "Minutes estimées",
    statRemaining: "Encore à faire",
    languageLabel: "Langue de réponse",
    languagePlaceholder: "Choisir la langue",
    recordingDevice: recordingDeviceCopy.fr,
    sectorLabel: "Secteur",
    sectorHint: "Les questions sont regroupées par secteur (santé, agriculture...).",
    sectorQuestions: "{count} questions",
    sectorsLoading: "Chargement des secteurs...",
    sectorsEmpty: "Aucun secteur disponible pour le moment.",
    sectorsError: "Impossible de charger les secteurs.",
    retry: "Réessayer",
    audioInstruction1: "Installez-vous dans un endroit calme.",
    audioInstruction2: "Lisez la question, puis répondez naturellement, comme dans une conversation.",
    audioInstruction3: "Réécoutez votre réponse et refaites-la si besoin avant de l'envoyer.",
    audioInstruction4: "Vos enregistrements restent sur cet appareil jusqu'à leur envoi.",
    textInstruction1: "Répondez dans la langue choisie, pas en français.",
    textInstruction2: "Utilisez le clavier de la langue pour les caractères spéciaux (ŋ, ɖ, ɛ, ɔ...).",
    textInstruction3: "Une réponse complète et naturelle vaut mieux qu'une traduction mot à mot.",
    textInstruction4: "Vous pouvez passer une question et y revenir plus tard.",
    start: "Commencer la session",
    resumeTitle: "Reprendre là où vous vous êtes arrêté",
    resumeProgress: "{answered} sur {total} envoyées",
    resumeSkipped: "{count} passée(s)",
    resumePausedOn: "En pause depuis le {date}",
    resume: "Reprendre",
    restart: "Recommencer",
    questionXofY: "Question {current} sur {total}",
    pause: "Mettre en pause",
    pausedToast: "Session mise en pause",
    pausedToastBody: "Vous pourrez reprendre là où vous vous êtes arrêté.",
    questionLabel: "Question en français",
    questionEmpty: "Le texte de cette question est indisponible.",
    questionFileOrder: "N° dans le fichier",
    localVersionLabel: "Déjà traduite dans la langue",
    alreadyDone: "Vous avez déjà envoyé une réponse à cette question.",
    answerLabel: "Votre réponse",
    answerIn: "Réponse en {language}",
    audioHint: "Appuyez sur Enregistrer, répondez, puis arrêtez.",
    recordingLive: "Enregistrement en cours",
    recordingPaused: "En pause, ce temps n'est pas compté",
    recorded: "Réponse enregistrée",
    micError: "Micro indisponible. Autorisez l'accès au microphone dans votre navigateur.",
    qualityMeasuring: "Vérification de la qualité du son...",
    qualityOk: "Qualité du son correcte.",
    qualityIssuesTitle: "Cet enregistrement ne peut pas être envoyé :",
    qualityRedoHint: "Réenregistrez dans un endroit calme, à une vingtaine de centimètres du micro.",
    qualityClipped: "le son sature (trop fort ou trop près du micro)",
    qualityMostlySilent: "l'enregistrement est presque entièrement silencieux",
    qualityTooQuiet: "la voix est trop faible",
    qualityNoisy: "le bruit de fond couvre la voix",
    qualityRejected: "Enregistrement refusé",
    textPlaceholder: "Écrivez la réponse en {language}...",
    keyboard: "Clavier",
    charCount: "{count} caractères",
    translationToggle: "Proposer une traduction de la question (facultatif)",
    translationPlaceholder: "La question en {language}...",
    record: "Enregistrer",
    stop: "Arrêter",
    redo: "Refaire",
    recordingPause: "Pause",
    recordingResume: "Reprendre",
    previous: "Précédente",
    skip: "Passer",
    send: "Envoyer",
    submitted: "Réponse envoyée",
    submitError: "L'envoi a échoué. Réessayez.",
    audioRequired: "Enregistrez une réponse avant de l'envoyer.",
    textRequired: "Écrivez une réponse avant de l'envoyer.",
    progressLabel: "Questions du secteur",
    legendCurrent: "En cours",
    legendDone: "Envoyées",
    legendSkipped: "Passées",
    legendWaiting: "En attente",
    questionsLoading: "Chargement des questions...",
    noQuestionsTitle: "Aucune question dans ce secteur",
    noQuestionsBody: "Ce secteur n'a pas encore de questions pour cette langue. Choisissez-en un autre.",
    changeSector: "Choisir un autre secteur",
    loadErrorTitle: "Impossible de charger les questions",
    loadErrorBody: "Vérifiez votre connexion puis réessayez.",
    completeTitle: "Merci pour votre contribution",
    completeBody: "{count} réponse(s) envoyée(s) pendant cette session.",
    allDoneBody: "Toutes les questions de ce secteur ont déjà une réponse dans cette langue.",
    statSent: "Envoyées",
    statSkipped: "Passées",
    reviewSkipped: "Reprendre les questions passées",
  },
  en: {
    audioTitle: "Answer aloud",
    textTitle: "Answer in writing",
    audioSubtitle: "Each question is written in French. Answer aloud in your language, one question at a time.",
    textSubtitle: "Each question is written in French. Write the answer in your language, one question at a time.",
    collectorNotice: "You are answering for profile {code}",
    collectorNoticeBody: "The answers you send are linked to this community profile.",
    statQuestions: "Questions",
    statMinutes: "Estimated minutes",
    statRemaining: "Still to do",
    languageLabel: "Answer language",
    languagePlaceholder: "Choose the language",
    recordingDevice: recordingDeviceCopy.en,
    sectorLabel: "Sector",
    sectorHint: "Questions are grouped by sector (health, farming...).",
    sectorQuestions: "{count} questions",
    sectorsLoading: "Loading sectors...",
    sectorsEmpty: "No sector available for now.",
    sectorsError: "Sectors could not be loaded.",
    retry: "Try again",
    audioInstruction1: "Find a quiet place.",
    audioInstruction2: "Read the question, then answer naturally, as in a conversation.",
    audioInstruction3: "Listen back to your answer and redo it if needed before sending it.",
    audioInstruction4: "Your recordings stay on this device until they are sent.",
    textInstruction1: "Answer in the chosen language, not in French.",
    textInstruction2: "Use the language keyboard for special characters (ŋ, ɖ, ɛ, ɔ...).",
    textInstruction3: "A full, natural answer is better than a word-for-word translation.",
    textInstruction4: "You can skip a question and come back to it later.",
    start: "Start the session",
    resumeTitle: "Pick up where you left off",
    resumeProgress: "{answered} of {total} sent",
    resumeSkipped: "{count} skipped",
    resumePausedOn: "Paused since {date}",
    resume: "Resume",
    restart: "Start over",
    questionXofY: "Question {current} of {total}",
    pause: "Pause",
    pausedToast: "Session paused",
    pausedToastBody: "You can pick up where you left off.",
    questionLabel: "Question in French",
    questionEmpty: "The text of this question is unavailable.",
    questionFileOrder: "Workbook no.",
    localVersionLabel: "Already translated in the language",
    alreadyDone: "You already sent an answer to this question.",
    answerLabel: "Your answer",
    answerIn: "Answer in {language}",
    audioHint: "Press Record, answer, then stop.",
    recordingLive: "Recording",
    recordingPaused: "Paused, this time is not counted",
    recorded: "Answer recorded",
    micError: "Microphone unavailable. Allow microphone access in your browser.",
    qualityMeasuring: "Checking the sound quality...",
    qualityOk: "Sound quality is fine.",
    qualityIssuesTitle: "This recording cannot be sent:",
    qualityRedoHint: "Record again in a quiet place, about twenty centimetres from the microphone.",
    qualityClipped: "the sound clips (too loud or too close to the microphone)",
    qualityMostlySilent: "the recording is almost entirely silent",
    qualityTooQuiet: "the voice is too quiet",
    qualityNoisy: "background noise covers the voice",
    qualityRejected: "Recording refused",
    textPlaceholder: "Write the answer in {language}...",
    keyboard: "Keyboard",
    charCount: "{count} characters",
    translationToggle: "Suggest a translation of the question (optional)",
    translationPlaceholder: "The question in {language}...",
    record: "Record",
    stop: "Stop",
    redo: "Redo",
    recordingPause: "Pause",
    recordingResume: "Resume",
    previous: "Previous",
    skip: "Skip",
    send: "Send",
    submitted: "Answer sent",
    submitError: "Sending failed. Try again.",
    audioRequired: "Record an answer before sending it.",
    textRequired: "Write an answer before sending it.",
    progressLabel: "Questions of the sector",
    legendCurrent: "Current",
    legendDone: "Sent",
    legendSkipped: "Skipped",
    legendWaiting: "Waiting",
    questionsLoading: "Loading questions...",
    noQuestionsTitle: "No question in this sector",
    noQuestionsBody: "This sector has no questions for this language yet. Choose another one.",
    changeSector: "Choose another sector",
    loadErrorTitle: "Questions could not be loaded",
    loadErrorBody: "Check your connection and try again.",
    completeTitle: "Thank you for your contribution",
    completeBody: "{count} answer(s) sent during this session.",
    allDoneBody: "Every question of this sector already has an answer in this language.",
    statSent: "Sent",
    statSkipped: "Skipped",
    reviewSkipped: "Go back to the skipped questions",
  },
} as const;

export type InitialDataCopy = {
  [Key in keyof typeof initialDataCopy.fr]:
    (typeof initialDataCopy.fr)[Key] extends string ? string : (typeof initialDataCopy.fr)[Key];
};
